/**
 * @file    useQueryCollections.ts
 * @scope   SHARED
 * @summary Owns the Medusa data-collection catalogue and the query-builder state: parses the
 *          collections returned by Guardrails, validates the user's queries and persists them
 *          into `customWidgetConfig.QueryCollections`.
 * @consumes Guardrails chart config payload, global store (`customWidget`, `setState`)
 * @produces availableCollections, queryData/setQueryData, queryIsValid, parseQueryCollections
 * @sideEffects Writes `customWidget.customWidgetConfig.QueryCollections` to the global store.
 * @notes Shared on purpose: the legacy Query Widget and the Enterprise V2 partition picker both
 *        consume `availableCollections`.
 */
import { useEffect, useState } from 'react';
import { SelectOptions } from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { IQueryData } from 'components/QueryBuilder/QueryBuilder';
import { QueryCollectionModel } from 'models/WidgetModel';
import { sortByString, SortOptions } from 'helpers/sorting';
import { strip } from 'helpers/utilities';

/** Everything the drawer needs from the query/collections domain. */
export type UseQueryCollectionsResult = {
  /** Collections offered by the query builder and the Enterprise partition picker. */
  availableCollections: SelectOptions[];
  /** Queries currently configured in the query builder. */
  queryData: IQueryData[];
  /** Setter handed to `QueryBuilder`. */
  setQueryData: React.Dispatch<React.SetStateAction<IQueryData[]>>;
  /** `false` while any query is empty — used by the Save guard. */
  queryIsValid: boolean;
  /** Parses a Guardrails chart config and refreshes `availableCollections`. */
  parseQueryCollections: (chartData: any) => void;
};

/**
 * @param customWidget Widget currently being edited.
 * @param setState     Global store setter.
 */
export const useQueryCollections = (
  customWidget: any,
  setState: (partial: any) => void
): UseQueryCollectionsResult => {
  const [queryData, setQueryData] = useState<IQueryData[]>([]);
  const [availableCollections, setAvailableCollections] = useState<SelectOptions[]>([]);
  const [queryIsValid, setQueryIsValid] = useState<boolean>(false);

  // Validate the builder's output and mirror it into the widget config on every change.
  useEffect(() => {
    if (
      queryData.length === 0 ||
      queryData.some((x) => x.query === '' || x.dataCollectionGuid === '')
    ) {
      setQueryIsValid(false);
      return;
    }

    const customWidgetConfig = customWidget.customWidgetConfig;

    if (customWidgetConfig) {
      const transformedQueryData: QueryCollectionModel[] = queryData.map((item) => ({
        queryId: item.query,
        queryName: item.dataCollectionGuid,
        createdDate: new Date().toISOString(),
      }));
      customWidgetConfig.queryCollections = transformedQueryData;
    }
    setQueryIsValid(true);
    setState({ customWidget: { ...customWidget, customWidgetConfig: customWidgetConfig } });
  }, [queryData]);

  /**
   * Maps the collections list coming from Guardrails to picker options.
   */
  const parseQueryCollections = (chartData: any) => {
    const collections: Array<any> = chartData?.dataCollectionsList?.length
      ? chartData.dataCollectionsList
      : [];

    const options: SelectOptions[] = collections.map((collection) => ({
      value: collection?.dataCollectionGuid,
      label: strip(collection?.dataCollectionName),
      longText: '',
      raw: '',
      originId: collection?.originId ?? '',
    }));

    sortByString(options, 'label', SortOptions.ASC);
    setAvailableCollections(options);
  };

  return {
    availableCollections,
    queryData,
    setQueryData,
    queryIsValid,
    parseQueryCollections,
  };
};
